import type { Item, Order, Payment, StockMove, Supplier } from "../../services/uniformService";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Uniforms: a catalogue of garments (one item per size and colour), family
 * orders that the office confirms (setting the stock aside), prepares and
 * hands over once paid, and the stock book behind it. The next steps here
 * mirror apps/uniforms/views.py.
 */

export const ugx = (value?: string | number | null) => `UGX ${Number(value || 0).toLocaleString()}`;
export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const balanceOf = (order: Pick<Order, "total_amount" | "paid_amount">) => Number(order.total_amount) - Number(order.paid_amount);
export const itemsLabel = (order: Pick<Order, "items">) => order.items.map((row) => `${row.uniform_item_name} (${row.uniform_item_size}) ×${row.quantity}`).join(", ");
export const pieces = (order: Pick<Order, "items">) => order.items.reduce((sum, row) => sum + row.quantity, 0);

type Pairs = [string, string][];
export const MOVE_TYPES: Pairs = [["purchase", "Received"], ["return", "Returned"], ["sale", "Set aside for an order"], ["damage", "Damaged or lost"], ["adjustment", "Stock-take correction"]];
export const labelOf = (pairs: Pairs, value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? (value || "—");
export const METHODS: Pairs = [["cash", "Cash"], ["mobile_money", "Mobile money"], ["bank_transfer", "Bank transfer"], ["card", "Card"]];

type Tone = { label: string; dot: string };
const tone = (label: string, dot: string): Tone => ({ label, dot });

// ---- Sizes ---------------------------------------------------------------------------

const LETTERS = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];
/** Sizes in the order a shop shelf keeps them: numbers and ages rising, then letter sizes. */
export function sizeRank(size: string) {
  const text = size.trim().toUpperCase();
  const letter = LETTERS.indexOf(text);
  if (letter >= 0) return 1000 + letter;
  const number = parseFloat(text);
  return Number.isNaN(number) ? 2000 : number;
}
export const bySize = (a: { size: string }, b: { size: string }) => sizeRank(a.size) - sizeRank(b.size) || a.size.localeCompare(b.size);

// ---- Stock ---------------------------------------------------------------------------

export function stockStatus(item: Pick<Item, "is_active" | "stock_quantity" | "minimum_stock">): Tone {
  if (!item.is_active) return tone("Retired", "bg-gray-400");
  if (item.stock_quantity === 0) return tone("Out of stock", "bg-red-500");
  if (item.minimum_stock != null && item.stock_quantity <= item.minimum_stock) return tone("Running low", "bg-amber-500");
  return tone("In stock", "bg-emerald-500");
}

/** Pieces free to promise: on the shelf, less what pending orders will take when confirmed. */
export const freeStock = (item: Pick<Item, "stock_quantity" | "on_order">) => Math.max(0, item.stock_quantity - (item.on_order ?? 0));

/** One garment in the shop: every size of the same item in the same colour. */
export interface Garment {
  key: string;
  name: string;
  color: string;
  category_name: string;
  description: string;
  sizes: Item[];
  inStock: number;
  low: number;
  high: number;
}

export function garments(items: Item[]): Garment[] {
  const groups = new Map<string, Garment>();
  for (const item of items) {
    const key = `${item.name.toLowerCase()}|${item.color.toLowerCase()}`;
    if (!groups.has(key)) groups.set(key, { key, name: item.name, color: item.color, category_name: item.category_name, description: item.description, sizes: [], inStock: 0, low: Infinity, high: 0 });
    const group = groups.get(key)!;
    group.sizes.push(item);
    group.inStock += item.stock_quantity;
    group.low = Math.min(group.low, Number(item.price));
    group.high = Math.max(group.high, Number(item.price));
    if (!group.description && item.description) group.description = item.description;
  }
  return [...groups.values()].map((group) => ({ ...group, sizes: [...group.sizes].sort(bySize) }));
}
export const priceRange = (garment: Pick<Garment, "low" | "high">) => (garment.low === garment.high ? ugx(garment.low) : `${ugx(garment.low)} – ${Number(garment.high).toLocaleString()}`);

/** What a basket of order lines costs. */
export function basketTotal(lines: { item: string; quantity: number }[], items: Item[]) {
  return lines.reduce((sum, line) => sum + Number(items.find((item) => String(item.id) === line.item)?.price ?? 0) * (line.quantity || 0), 0);
}

// ---- Orders --------------------------------------------------------------------------

export function orderStatus(order: Pick<Order, "status" | "total_amount" | "paid_amount">): Tone {
  const owing = balanceOf(order) > 0;
  switch (order.status) {
    case "pending": return tone("Waiting to confirm", "bg-amber-500");
    case "confirmed":
    case "processing": return tone("Being prepared", "bg-indigo-500");
    case "ready": return owing ? tone("Ready, awaiting payment", "bg-amber-500") : tone("Ready to collect", "bg-emerald-400");
    case "delivered": return tone("Handed over", "bg-emerald-600");
    default: return tone("Cancelled", "bg-gray-400");
  }
}

export function paymentState(order: Pick<Order, "status" | "total_amount" | "paid_amount">) {
  if (order.status === "cancelled") return Number(order.paid_amount) ? "Refund due" : "—";
  const owing = balanceOf(order);
  if (owing <= 0) return "Paid";
  return Number(order.paid_amount) ? `${ugx(owing)} still to pay` : "Not paid";
}

type Step = { action: "confirm" | "process" | "deliver"; label: string };

/** What a person can do with an order now. `office` is the uniform office's view. */
export function orderActions(order: Order, office: boolean) {
  const open = order.status === "pending" || order.status === "confirmed" || order.status === "ready";
  const unpaid = Number(order.paid_amount) === 0;
  let next: Step | null = null;
  if (office) {
    if (order.status === "pending") next = { action: "confirm", label: "Confirm and set aside" };
    else if (order.status === "confirmed" || order.status === "processing") next = { action: "process", label: "Mark ready" };
    else if (order.status === "ready" && balanceOf(order) <= 0) next = { action: "deliver", label: "Hand over" };
  }
  return {
    next,
    // The office can cancel until it is handed over; a family only before the office confirms it. Paid orders are refunded first.
    cancel: unpaid && (office ? open : order.status === "pending"),
    needsRefund: !unpaid && open,
    waitingForPayment: order.status === "ready" && balanceOf(order) > 0,
  };
}

// ---- Registers -------------------------------------------------------------------------

export function orderConfig(office: boolean): RegisterConfig<Order> {
  const open = (row: Order) => row.status !== "delivered" && row.status !== "cancelled";
  return {
    search: (row) => `${row.order_number} ${row.student_name} ${row.student_number ?? ""} ${row.student_class ?? ""} ${itemsLabel(row)} ${row.notes}`,
    views: office
      ? [
          { key: "open", label: "Open", match: open },
          { key: "pending", label: "To confirm", match: (row) => row.status === "pending" },
          { key: "preparing", label: "Being prepared", match: (row) => row.status === "confirmed" || row.status === "processing" },
          { key: "ready", label: "Ready", match: (row) => row.status === "ready" },
          { key: "owing", label: "Owing", match: (row) => open(row) && balanceOf(row) > 0 },
          { key: "delivered", label: "Handed over", match: (row) => row.status === "delivered" },
          { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
          { key: "all", label: "All" },
        ]
      : [
          { key: "open", label: "Open", match: open },
          { key: "owing", label: "To pay", match: (row) => open(row) && balanceOf(row) > 0 },
          { key: "ready", label: "Ready to collect", match: (row) => row.status === "ready" },
          { key: "all", label: "All" },
        ],
    date: { label: "Ordered", value: (row) => row.order_date },
    category: { label: "Class", value: (row) => row.student_class },
    sorts: [
      { key: "oldest", label: "Oldest first", compare: byText((row) => row.order_date) },
      { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.order_date)) },
      { key: "balance", label: "Most owing", compare: reversed(byNumber(balanceOf)) },
      { key: "name", label: "Learner A–Z", compare: byText((row) => row.student_name) },
    ],
  };
}

export const stockConfig: RegisterConfig<Item> = {
  search: (row) => `${row.name} ${row.size} ${row.color} ${row.category_name} ${row.supplier ?? ""}`,
  views: [
    { key: "active", label: "In the catalogue", match: (row) => row.is_active },
    { key: "low", label: "Running low", match: (row) => row.is_active && row.stock_quantity > 0 && row.stock_quantity <= (row.minimum_stock ?? 0) },
    { key: "out", label: "Out of stock", match: (row) => row.is_active && row.stock_quantity === 0 },
    { key: "short", label: "Orders need more", match: (row) => row.is_active && (row.on_order ?? 0) > row.stock_quantity },
    { key: "retired", label: "Retired", match: (row) => !row.is_active },
    { key: "all", label: "All" },
  ],
  category: { label: "Kind", value: (row) => row.category_name },
  sorts: [
    { key: "name", label: "Name and size", compare: (a, b) => a.name.localeCompare(b.name) || a.color.localeCompare(b.color) || bySize(a, b) },
    { key: "stock", label: "Least stock first", compare: byNumber((row) => row.stock_quantity) },
    { key: "sold", label: "Best sellers", compare: reversed(byNumber((row) => row.sold ?? 0)) },
  ],
};

export const garmentConfig: RegisterConfig<Garment> = {
  search: (row) => `${row.name} ${row.color} ${row.category_name} ${row.description} ${row.sizes.map((item) => item.size).join(" ")}`,
  views: [
    { key: "stock", label: "In stock", match: (row) => row.inStock > 0 },
    { key: "all", label: "All" },
  ],
  category: { label: "Kind", value: (row) => row.category_name },
  sorts: [
    { key: "name", label: "Name A–Z", compare: byText((row) => row.name) },
    { key: "price", label: "Cheapest first", compare: byNumber((row) => row.low) },
  ],
};

export const moveConfig: RegisterConfig<StockMove> = {
  search: (row) => `${row.uniform_item_name} ${row.uniform_item_size} ${row.uniform_item_color} ${row.reference_number} ${row.notes} ${row.processed_by_name ?? ""}`,
  views: [
    { key: "all", label: "All" },
    { key: "in", label: "Stock in", match: (row) => row.quantity > 0 },
    { key: "out", label: "Stock out", match: (row) => row.quantity < 0 },
    { key: "written_off", label: "Written off", match: (row) => row.transaction_type === "damage" },
    { key: "corrections", label: "Corrections", match: (row) => row.transaction_type === "adjustment" },
  ],
  date: { label: "Date", value: (row) => row.transaction_date },
  category: { label: "Kind", value: (row) => labelOf(MOVE_TYPES, row.transaction_type) },
  sorts: [
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.transaction_date)) },
    { key: "oldest", label: "Oldest first", compare: byText((row) => row.transaction_date) },
  ],
};

export const supplierConfig: RegisterConfig<Supplier> = {
  search: (row) => `${row.name} ${row.contact_person} ${row.phone} ${row.email} ${row.address}`,
  views: [
    { key: "active", label: "Active", match: (row) => row.is_active },
    { key: "inactive", label: "Not used", match: (row) => !row.is_active },
    { key: "all", label: "All" },
  ],
  sorts: [{ key: "name", label: "Name A–Z", compare: byText((row) => row.name) }],
};

export const paymentConfig: RegisterConfig<Payment> = {
  search: (row) => `${row.order_number} ${row.student_name ?? ""} ${row.transaction_id} ${row.receipt_number ?? ""}`,
  views: [
    { key: "all", label: "All" },
    { key: "completed", label: "Received", match: (row) => row.payment_status === "completed" },
    { key: "refunded", label: "Refunded", match: (row) => row.payment_status === "refunded" },
  ],
  date: { label: "Paid", value: (row) => row.payment_date },
  category: { label: "Method", value: (row) => labelOf(METHODS, row.payment_method) },
  sorts: [
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.payment_date)) },
    { key: "amount", label: "Largest first", compare: reversed(byNumber((row) => row.amount)) },
  ],
};
